import { useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { Card, CardContent, CircularProgress, Typography, Box, Divider } from '@mui/material';

// Hooks
import useAuthScope from 'hooks/useAuthScope';
import useDataSourceManager from 'hooks/useDataSourceManager';

// Components
import DrawerField from 'components/Drawer';
import GlobalAlert from 'components/Notification';

import CustomTooltip from './components/CustomTooltip';
import SignalQualityBarChart from './components/SignalQualityBarChart';
import WirelessCoverageInfo from './components/WirelessCoverageInfo';

// Const
import { defaultWirelessCoverageInfo } from './const';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.svg';
import icn_display_clock from 'assets/images/icons/icn_display_clock.svg';

const WirelessCoverage = () => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [wirelessCoverageUuid, _setWirelessCoverageUuid] = useState(uuidv4());
  const [wirelessCoverageInfo, setWirelessCoverageInfo] = useState(defaultWirelessCoverageInfo);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wirelessCoverageNoData, setWirelessCoverageNoData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const fetchWirelessCoverage = () => {
    const apiUrl = standalone
      ? `/orgs/:orgId/sites/:siteId/wirelessCoverage/signalQualification.read`
      : `/ents/:entId/orgs/:orgId/sites/:siteId/wirelessCoverage/signalQualification.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
      params: {},
    };
  };

  const parseWirelessCoverageInfo = signalQualityData => {
    const { totalClient = '-', meanRSSI = '-', weakPercent = '-', threshold = 0, series = [] } = signalQualityData || {};

    let statusLabel = '-';
    if (typeof meanRSSI === 'number') {
      if (meanRSSI >= -65) {
        statusLabel = 'Good';
      } else if (meanRSSI >= -75 && meanRSSI < -65) {
        statusLabel = 'Warning';
      } else if (meanRSSI < -75) {
        statusLabel = 'Critical';
      }
    }

    if (statusLabel !== '-' && typeof weakPercent === 'number') {
      if (statusLabel === 'Good') {
        if (weakPercent < 5) {
          statusLabel = 'Good';
        } else if (weakPercent > 5 && weakPercent <= 15) {
          statusLabel = 'Warning';
        } else if (weakPercent > 15) {
          statusLabel = 'Critical';
        }
      } else if (statusLabel === 'Warning') {
        if (weakPercent > 5 && weakPercent <= 15) {
          statusLabel = 'Warning';
        } else if (weakPercent > 15) {
          statusLabel = 'Critical';
        }
      } else if (statusLabel === 'Critical') {
        if (weakPercent > 15) {
          statusLabel = 'Critical';
        }
      }
    }

    const formattedSeries = series.map(item => ({
      ...item,
      rssi: `${item.rssi} dBm`,
    }));

    setWirelessCoverageInfo({
      originData: signalQualityData,
      totalClient: typeof totalClient === 'number' && totalClient >= 0 ? totalClient : '-',
      status: typeof meanRSSI === 'number' && typeof weakPercent === 'number' ? statusLabel : '-',
      meanRSSI: typeof meanRSSI === 'number' ? `${meanRSSI} dBm` : '-',
      weakPercent: typeof weakPercent === 'number' ? `${weakPercent}%` : '-',
      threshold: typeof threshold === 'number' ? `${threshold} dBm` : '-',
      series: formattedSeries,
    });
  };

  const reloadPage = data => {
    if (data) {
      parseWirelessCoverageInfo(data); // Mockup
    } else {
      init();
    }
  };

  const checkUiDisplayNoData = (wirelessCoverageRes) => {
    const isApCountZero = Number(wirelessCoverageRes?.data?.apCount) === 0;
    // const isAllZero = wirelessCoverageRes?.data?.series?.every(item => item.count === 0);

    return isApCountZero;
  }

  const init = async () => {
    setIsLoading(true);

    const wirelessCoverageParams = fetchWirelessCoverage();

    const { getSendBatchData } = await sendBatch([{ ...wirelessCoverageParams, requestUuid: wirelessCoverageUuid }]);

    const wirelessCoverageRes = getSendBatchData({ requestUuid: wirelessCoverageUuid });

    const isWirelessCoverageNoData = checkUiDisplayNoData(wirelessCoverageRes);

    if (isWirelessCoverageNoData) {
      setWirelessCoverageNoData(true);
    } else if (wirelessCoverageRes?.data) {
      parseWirelessCoverageInfo(wirelessCoverageRes?.data);
      setWirelessCoverageNoData(false);
    } else if (['AI Insight subscription required', 'UnityPro subscription required'].includes(wirelessCoverageRes?.message)) {
      setWirelessCoverageNoData(true);
    } else {
      setAlertInfo({
        open: true,
        severity: 'error',
        message: wirelessCoverageRes?.message || 'Error',
      });
      setWirelessCoverageInfo(cloneDeep(defaultWirelessCoverageInfo));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!isAuthReady) return;
    init();
  }, [isAuthReady]);

  return (
    <Box>
      <Card
        sx={{ maxWidth: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', cursor: wirelessCoverageInfo?.series.length > 0 && 'pointer' }}
        onClick={() => wirelessCoverageInfo?.series.length > 0 && setDrawerOpen(true)}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={icn_linebar_chart} alt="icn_line_chart" />
              <Typography sx={{ marginLeft: 1, marginRight: 1, color: '#172664', fontWeight: 400, fontSize: '15px' }}>Wireless Signal Quality</Typography>
              <CustomTooltip />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={icn_display_clock} alt="icn_display_clock" />
              <Typography sx={{ marginLeft: '5px', marginRight: '20px', color: '#172664', fontWeight: 400, fontSize: '14px' }}>Last 24 Hours</Typography>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {wirelessCoverageNoData ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 200 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#aaaaaa' }}>No data available</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'stretch' },
                  }}
                >
                  <Box sx={{ width: { xs: '100%', md: 340, marginTop: 8 } }}>
                    <WirelessCoverageInfo wirelessCoverageInfo={wirelessCoverageInfo} />
                  </Box>

                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      mx: 3,
                      borderColor: '#e0e0e0',
                    }}
                  />
                  <Divider
                    sx={{
                      display: { xs: 'block', md: 'none' },
                      my: 2,
                      borderColor: '#e0e0e0',
                    }}
                  />

                  <Box
                    sx={{
                      flex: 1,
                      height: 260,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                    }}
                  >
                    {wirelessCoverageInfo?.series.length > 0 ? (
                      <SignalQualityBarChart barChartData={wirelessCoverageInfo?.series} />
                    ) : (
                      <Typography sx={{ fontSize: 14, fontWeight: 400, lineHeight: 1.5, color: '#aaaaaa' }}>No data available</Typography>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {drawerOpen && (
        <DrawerField
          width="580px"
          open={drawerOpen}
          handleClose={() => setDrawerOpen(false)}
          contentKey={`AInsight_Wireless_Coverage_Drawer_${uuidv4()}`}
          commonContent="ainsightWirelessCoverageDrawer"
          contentProps={{
            setDrawerOpen,
            auth,
            standalone,
            wirelessCoverageInfo,
            reloadPage,
          }}
        />
      )}

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};

export default WirelessCoverage;
